import React, { useContext, useState } from 'react';
import { Container, Header, Icon, Menu, Segment } from 'semantic-ui-react';
import { UserContext } from '../../context/User';
import { StatusContext } from '../../context/Status';

const CodeBlock = ({ children }) => (
  <pre
    style={{
      background: '#f6f8fa',
      color: '#24292e',
      border: '1px solid #d0d7de',
      borderRadius: '8px',
      padding: '16px',
      overflowX: 'auto',
      fontSize: '13px',
      lineHeight: '1.6',
      margin: '12px 0',
    }}
  >
    <code>{children}</code>
  </pre>
);

const Section = ({ id, icon, title, children }) => (
  <Segment id={id} style={{ marginBottom: '24px', scrollMarginTop: '80px' }}>
    <Header as='h3'>
      <Icon name={icon} />
      <Header.Content>{title}</Header.Content>
    </Header>
    {children}
  </Segment>
);

const Note = ({ children }) => (
  <div
    style={{
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '6px',
      padding: '10px 14px',
      margin: '10px 0',
      fontSize: '14px',
      color: '#1e40af',
    }}
  >
    <Icon name='info circle' />
    {children}
  </div>
);

const sections = [
  { id: 'overview', icon: 'map', title: '概览' },
  { id: 'get-key', icon: 'key', title: '获取 API Key' },
  { id: 'claude-code', icon: 'terminal', title: '在 Claude Code 中使用' },
  { id: 'codex', icon: 'code', title: '在 OpenAI Codex CLI 中使用' },
  { id: 'cursor', icon: 'pencil', title: '在 Cursor 中使用' },
  // { id: 'openai-sdk', icon: 'puzzle', title: '在 OpenAI SDK 中使用' },
  // { id: 'anthropic-sdk', icon: 'puzzle', title: '在 Anthropic SDK 中使用' },
];

const Doc = () => {
  const [userState] = useContext(UserContext);
  const [statusState] = useContext(StatusContext);
  const [activeSection, setActiveSection] = useState('overview');

  const systemName = statusState?.status?.system_name || 'MoRouter';
  const apiBase = "https://api.morouter.com";

  const scrollTo = (id) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  if (!userState.user) {
    return (
      <Container style={{ paddingTop: '40px', textAlign: 'center' }}>
        <Segment>
          <Icon name='lock' size='huge' color='grey' />
          <Header as='h3' color='grey'>
            请先登录后查看使用文档
          </Header>
        </Segment>
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '20px', paddingBottom: '40px' }}>
      <Header as='h2' style={{ marginBottom: '4px' }}>
        <Icon name='book' />
        <Header.Content>
          使用文档
          <Header.Subheader>
            如何在各类 AI 工具中接入 {systemName}
          </Header.Subheader>
        </Header.Content>
      </Header>

      <div
        style={{
          display: 'flex',
          gap: '24px',
          alignItems: 'flex-start',
          marginTop: '20px',
        }}
      >
        {/* Sidebar nav */}
        <div
          style={{
            width: '200px',
            flexShrink: 0,
            position: 'sticky',
            top: '80px',
          }}
        >
          <Menu vertical fluid secondary>
            {sections.map((s) => (
              <Menu.Item
                key={s.id}
                active={activeSection === s.id}
                onClick={() => scrollTo(s.id)}
                style={{ cursor: 'pointer' }}
              >
                <Icon name={s.icon} />
                {s.title}
              </Menu.Item>
            ))}
          </Menu>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Section id='overview' icon='map' title='概览'>
            <p>
              {systemName} 是一个兼容 OpenAI API
              格式的模型聚合平台。你可以用它替换任何支持自定义 API 地址的 AI
              工具中的官方 endpoint，从而统一管理多个模型的访问与计费。
            </p>
            <p>接入只需两步：</p>
            <ol>
              <li>在「令牌」页面创建一个 API Key</li>
              <li>
                将工具的 <code>base_url</code> 改为{' '}
                <strong>{apiBase}/v1</strong>，API Key 填入你创建的令牌
              </li>
            </ol>
            <Note>
              {systemName} 兼容 OpenAI 格式，也支持 Anthropic
              格式，请在模型详情中查看。大多数工具只需修改 base URL 和 API Key 即可使用。
            </Note>
          </Section>

          <Section id='get-key' icon='key' title='获取 API Key'>
            <p>登录后，点击顶部导航栏的「令牌」，然后：</p>
            <ol>
              <li>点击「添加令牌」</li>
              <li>
                填写名称（如 <em>claude-code</em>），按需设置额度和过期时间
              </li>
              <li>
                点击「提交」，复制生成的 <code>sk-...</code> 令牌
              </li>
            </ol>
            <p>
              后续所有工具中的 <strong>API Key</strong> 均填写此令牌。
            </p>
          </Section>

          <Section
            id='cc-switch'
            icon='terminal'
            title='推荐在 cc-switch中使用'
          >
            <CodeBlock>
              <ol>
                <li>请先在官方下载客户端：<a href='https://ccswitch.io/zh/'>https://ccswitch.io/zh/</a></li>
                <li>参考官方文档，配置你想使用的 agent 类型（claudecode/codex/cursor）</li>
                <li>在自定义供应商中填写：
                  <ol>
                    <li>请求地址为 <b>https://api.morouter.com</b></li>
                    <li>API key 为 <b>$YOUR_API_KEY</b></li>
                    <li>API 格式 为 <b>OpenAl Chat Completions(需开启路由)</b></li>
                  </ol>
                </li>
              </ol>
            </CodeBlock>
          </Section>

{/*           <Section */}
{/*             id='claude-code' */}
{/*             icon='terminal' */}
{/*             title='在 Claude Code 中使用' */}
{/*           > */}
{/*             <p>参考 <b>~/.claude/settings.json</b> 配置如下</p> */}
{/*             <CodeBlock> */}
{/*               {`{ */}
{/*   "env": { */}
{/*     "DISABLE_TELEMETRY": "1", */}
{/*     "ANTHROPIC_AUTH_TOKEN": "$YOUR_API_KEY", */}
{/*     "ANTHROPIC_BASE_URL": "https://api.morouter.com", */}
{/*     "DISABLE_AUTOUPDATER": "1" */}
{/*   }, */}
{/*   "effortLevel": "medium", */}
{/*   "autoUpdatesChannel": "latest" */}
{/* }`} */}
{/*             </CodeBlock> */}
{/*           </Section> */}
{/**/}
{/*           <Section id='codex' icon='code' title='在 OpenAI Codex CLI 中使用'> */}
{/*             <CodeBlock>TODO</CodeBlock> */}
{/*           </Section> */}
{/**/}
{/*           <Section id='cursor' icon='pencil' title='在 Cursor 中使用'> */}
{/*             <CodeBlock>TODO</CodeBlock> */}
{/*           </Section> */}
        </div>
      </div>
    </Container>
  );
};

export default Doc;
